<template>
  <JoszakiModal
    :open="open"
    :title="$t('oauthLink.title')"
    :confirm-button-label="$t('oauthLink.submitButton')"
    :loading="submitting"
    @cancel="cancel"
    @close="cancel"
    @confirm="submit"
  >
    <template #body>
      <p class="mb-4">
        <i18n path="oauthLink.description">
          <template #email>
            <strong>{{ profile.email }}</strong>
          </template>
        </i18n>
      </p>
      <JoszakiInput
        v-model="password"
        type="password"
        :label="$t('oauthLink.passwordLabel')"
        :placeholder="$t('registration.password.placeholder')"
        :validation-state="v$.password"
        @keydown.enter.prevent="submit"
      />
      <div v-if="requestError" class="mt-2">
        <JoszakiPill type="warning" class="text-xs">{{
          requestError
        }}</JoszakiPill>
      </div>
    </template>
  </JoszakiModal>
</template>

<script>
import { ref, computed, useContext } from "@nuxtjs/composition-api";
import { useVuelidate } from "@vuelidate/core";
import { usePostLogin } from "~/components/_refactored/auth/composables/useLogin";
import { useOAuthStore } from "~/components/_refactored/auth/composables/useOAuthStore";
import LINK_OAUTH_PROVIDER_MUTATION from "~/graphql/linkOauthProvider.graphql";

export default {
  name: "OAuthLinkPrompt",
  setup() {
    const {
      $validation: { rules },
      $mutate,
      $logger,
      i18n,
    } = useContext();
    const { linkingToken, profile: oauthProfile, needsLinking, clear } =
      useOAuthStore();
    const postLogin = usePostLogin();

    const password = ref("");
    const submitting = ref(false);
    const requestError = ref(null);

    const open = needsLinking;
    const profile = computed(
      () => oauthProfile.value || { email: "", name: "" }
    );

    const v$ = useVuelidate(
      { password: { required: rules.required } },
      { password },
      { $lazy: true, $stopPropagation: true }
    );

    function cancel() {
      if (submitting.value) return;
      password.value = "";
      requestError.value = null;
      v$.value.$reset();
      clear();
    }

    async function submit() {
      requestError.value = null;
      const valid = await v$.value.$validate();
      if (!valid) return;

      try {
        submitting.value = true;
        const resp = await $mutate(LINK_OAUTH_PROVIDER_MUTATION, {
          linkingToken: linkingToken.value,
          password: password.value,
        });

        if (resp.userErrors && resp.userErrors.length) {
          const code = resp.userErrors[0].message;
          requestError.value = i18n.te(`error.messages.${code}`)
            ? i18n.t(`error.messages.${code}`)
            : i18n.t("oauthLink.genericError");
          return;
        }

        const { token, personId, isMate } = resp;
        clear();
        password.value = "";
        await postLogin({ token, personId, isMate });
      } catch (e) {
        $logger.error("OAuth link failed", e);
        requestError.value =
          typeof e === "string" && e ? e : i18n.t("oauthLink.genericError");
      } finally {
        submitting.value = false;
      }
    }

    return {
      open,
      profile,
      password,
      v$,
      submitting,
      requestError,
      submit,
      cancel,
    };
  },
};
</script>
